import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useBeta } from '../../../contexts/BetaContext.jsx';

/**
 * 公開ページ用レイアウト
 * ランディング、製品紹介、料金、認証ページ用
 */
const MOBILE_MENU_CLOSE_DURATION_MS = 200;

const PublicLayout = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);
  const { isBetaPeriod, isHubSettingsReady } = useBeta();
  const showBetaLabel = isHubSettingsReady && isBetaPeriod === true;
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ページ遷移時にモバイルメニューを閉じる（アニメーションなし）
  useEffect(() => {
    setMobileMenuOpen(false);
    setMenuClosing(false);
  }, [location.pathname]);

  // ページ遷移時にスクロール位置を最上部に戻す
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // 認証ページかどうか
  const isAuthPage = [
    '/login',
    '/signup',
    '/reset-password',
    '/reset-password/confirm',
    '/verify-email',
    '/verify-email/pending',
  ].includes(location.pathname);

  // 認証ページではNavbarを表示しない
  if (isAuthPage) {
    return (
      <div className="hub-page public-layout">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="hub-page public-layout">
      {/* ナビゲーションバー */}
      <nav className={`public-navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="public-navbar-inner">
          {/* ロゴ */}
          <Link to="/" className="public-navbar-logo">
            <img src="/logo.webp" alt="Synapse" className="public-navbar-logo-img" />
            <span>Synapse{showBetaLabel ? ' β版' : ''}</span>
          </Link>

          {/* ナビゲーションリンク */}
          <div className="public-navbar-nav">
            <Link 
              to="/products" 
              className={`public-navbar-link ${location.pathname === '/products' ? 'active' : ''}`}
            >
              製品
            </Link>
            <Link 
              to="/pricing" 
              className={`public-navbar-link ${location.pathname === '/pricing' ? 'active' : ''}`}
            >
              料金
            </Link>
            <Link 
              to="/contact" 
              className={`public-navbar-link ${location.pathname === '/contact' ? 'active' : ''}`}
            >
              お問い合わせ
            </Link>
          </div>

          {/* アクションボタン */}
          <div className="public-navbar-actions">
            <Link to="/login" className="hub-btn hub-btn-ghost">ログイン</Link>
            <Link to="/signup" className="hub-btn hub-btn-gradient">無料で始める</Link>
          </div>

          {/* モバイルメニューボタン */}
          <button 
            className="public-navbar-mobile-btn"
            onClick={() => {
              if (mobileMenuOpen) {
                setMenuClosing(true);
                setTimeout(() => {
                  setMobileMenuOpen(false);
                  setMenuClosing(false);
                }, MOBILE_MENU_CLOSE_DURATION_MS);
              } else {
                setMobileMenuOpen(true);
              }
            }}
            aria-label={mobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
          >
            {mobileMenuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            )}
          </button>
        </div>

        {/* モバイルメニュー */}
        {mobileMenuOpen && (
          <div
            className={`public-navbar-mobile-menu ${menuClosing ? 'public-navbar-mobile-menu--closing' : ''}`}
          >
            <Link to="/products" className="public-navbar-mobile-link">製品</Link>
            <Link to="/pricing" className="public-navbar-mobile-link">料金</Link>
            <Link to="/contact" className="public-navbar-mobile-link">お問い合わせ</Link>
            <div className="public-navbar-mobile-divider" />
            <Link to="/login" className="public-navbar-mobile-link">ログイン</Link>
            <Link to="/signup" className="hub-btn hub-btn-gradient" style={{ width: '100%', justifyContent: 'center' }}>
              無料で始める
            </Link>
          </div>
        )}
      </nav>

      {/* メインコンテンツ */}
      <main className="public-content">
        <Outlet />
      </main>

      {/* フッター */}
      <footer className="hub-footer">
        <div className="hub-footer-inner">
          <div className="hub-footer-top">
            {/* ブランド */}
            <div className="hub-footer-brand">
              <div className="hub-footer-logo">
                <img src="/logo.webp" alt="Synapse" className="hub-footer-logo-img" />
                <span>Synapse</span>
              </div>
              <p className="hub-footer-description">
                歯科医療のDXを推進する総合SaaSプラットフォーム。
                最新のテクノロジーで診療の質と効率を向上させます。
              </p>
            </div>

            {/* 製品 */}
            <div className="hub-footer-nav">
              <h4>製品</h4>
              <ul>
                <li><Link to="/products">製品一覧</Link></li>
                <li><Link to="/pricing">料金プラン</Link></li>
              </ul>
            </div>

            {/* サポート */}
            <div className="hub-footer-nav">
              <h4>サポート</h4>
              <ul>
                <li><a href="#">ドキュメント</a></li>
                <li><Link to="/contact">お問い合わせ</Link></li>
              </ul>
            </div>

            {/* 会社情報 */}
            <div className="hub-footer-nav">
              <h4>会社情報</h4>
              <ul>
                <li><a href="https://www.converge-p.co.jp" target="_blank" rel="noopener noreferrer">ConvergePartners Inc.</a></li>
                <li><Link to="/privacy">プライバシーポリシー</Link></li>
                <li><Link to="/terms">利用規約</Link></li>
                <li><Link to="/commerce">特定商取引法に基づく表記</Link></li>
                <li><Link to="/crossborder">外国にある第三者への提供に関する公表事項</Link></li>
              </ul>
            </div>
          </div>

          <div className="hub-footer-bottom">
            <p className="hub-footer-copyright">
              © {currentYear} Synapse by{' '}
              <a href="https://www.converge-p.co.jp" target="_blank" rel="noopener noreferrer">
                ConvergePartners Inc.
              </a>{' '}
              All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;

